import { useState, useRef, useCallback, useEffect, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import api from '../services/api'
import { useListaFlutuante } from '../hooks/useListaFlutuante'

interface Cidade {
  cid_cod: number
  cid_des: string
  cid_codmun: number
  cid_estcod: string
}

interface Props {
  codValue: string
  desValue: string
  ibgeValue: string
  onChange: (cod: string, des: string, ibge: string, uf: string) => void
  ufValue?: string
  filtroUf?: string  // restringe busca a uma UF específica
}

export default function CidadeSelect({ codValue, desValue, ibgeValue, ufValue, filtroUf, onChange }: Props) {
  const [aberta, setAberta] = useState(false)
  const [filtro, setFiltro] = useState('')
  const [resultados, setResultados] = useState<Cidade[]>([])
  const [buscando, setBuscando] = useState(false)
  const [localCod, setLocalCod] = useState(codValue)
  const [aviso, setAviso] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const descRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { posicao, listaRef } = useListaFlutuante(ref, aberta, () => setAberta(false))

  useEffect(() => { setLocalCod(codValue); if (codValue) setAviso('') }, [codValue])

  const buscar = useCallback((valor: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (valor.length < 2) { setResultados([]); return }
    debounceRef.current = setTimeout(async () => {
      setBuscando(true)
      try {
        const r = await api.get('/erp/cidades', { params: { busca: valor, ...(filtroUf ? { uf: filtroUf } : {}) } })
        setResultados(r.data)
      } finally {
        setBuscando(false)
      }
    }, 350)
  }, [filtroUf])

  async function buscarPorCodigo(cod: string) {
    if (!cod.trim()) { onChange('', '', '', ''); setAviso(''); return }
    try {
      const r = await api.get('/erp/cidades', { params: { busca: cod.trim(), ...(filtroUf ? { uf: filtroUf } : {}) } })
      const exato = r.data.find((c: Cidade) => String(c.cid_cod) === cod.trim())
      const c: Cidade | null = exato ?? (r.data.length === 1 ? r.data[0] : null)
      if (c) {
        onChange(String(c.cid_cod), c.cid_des, String(c.cid_codmun), c.cid_estcod || '')
        setLocalCod(String(c.cid_cod))
        setAviso('')
      } else {
        onChange('', '', '', '')
        setAviso(filtroUf ? `Código "${cod.trim()}" não encontrado em ${filtroUf} (CT-e intermunicipal exige origem e destino na mesma UF)` : `Código "${cod.trim()}" não encontrado`)
      }
    } catch {
      onChange('', '', '', '')
      setAviso(filtroUf ? `Código "${cod.trim()}" não encontrado em ${filtroUf} (CT-e intermunicipal exige origem e destino na mesma UF)` : `Código "${cod.trim()}" não encontrado`)
    }
  }

  function selecionar(c: Cidade) {
    onChange(String(c.cid_cod), c.cid_des, String(c.cid_codmun), c.cid_estcod || '')
    setLocalCod(String(c.cid_cod))
    setAviso('')
    setAberta(false)
    setFiltro('')
    setResultados([])
    descRef.current?.focus() // lista fica no body; sem isso o Tab recomeça do topo da página
  }

  // Tab/Esc no filtro fecha a lista e devolve o foco pro campo
  function fecharVoltandoFoco(e: KeyboardEvent) {
    if (e.key !== 'Tab' && e.key !== 'Escape') return
    e.preventDefault()
    setAberta(false)
    descRef.current?.focus()
  }

  return (
    <div className="nat-wrap" ref={ref}>
      <div className="nat-input-row">
        <input
          className="nat-cod-input"
          type="text"
          placeholder="Cód."
          value={localCod}
          style={aviso ? { borderColor: '#dc2626' } : undefined}
          onChange={e => { setLocalCod(e.target.value.replace(/\D/g, '')); setAviso('') }}
          onBlur={e => {
            if (e.target.value) buscarPorCodigo(e.target.value)
            else { onChange('', '', '', ''); setAviso('') }
          }}
          onKeyDown={e => { if (e.key === 'Enter') buscarPorCodigo(localCod) }}
        />
        <div
          ref={descRef}
          className="nat-desc-box"
          tabIndex={0}
          onClick={() => { setAberta(o => !o); setFiltro(''); setResultados([]) }}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAberta(o => !o); setFiltro(''); setResultados([]) } }}
        >
          {desValue ? (
            <>
              <span className="nat-dsc">{desValue}{ufValue ? ` / ${ufValue}` : ''}</span>
              {ibgeValue && ibgeValue !== '0' && (
                <span className="nat-cfop" style={{ flexShrink: 0, fontSize: '0.75rem' }}>IBGE {ibgeValue}</span>
              )}
            </>
          ) : (
            <span className="nat-placeholder">Selecione a cidade...</span>
          )}
          <span className="nat-chevron">{aberta ? '▴' : '▾'}</span>
        </div>
      </div>
      {aviso && <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.2rem', paddingLeft: '0.25rem' }}>{aviso}</div>}

      {aberta && posicao && createPortal(
        <div ref={listaRef} className="nat-lista" style={{ position: 'fixed', ...posicao, minWidth: posicao.width }}>
          <div className="nat-filtro-wrap">
            <input
              autoFocus
              className="nat-filtro"
              placeholder="Buscar por código ou nome da cidade..."
              value={filtro}
              onChange={e => { setFiltro(e.target.value); buscar(e.target.value) }}
              onKeyDown={fecharVoltandoFoco}
            />
          </div>
          <div className="nat-itens">
            {buscando && <div className="nat-vazio">Buscando...</div>}
            {!buscando && filtro.length >= 2 && resultados.length === 0 && (
              <div className="nat-vazio">Nenhum resultado</div>
            )}
            {!buscando && filtro.length < 2 && (
              <div className="nat-vazio" style={{ color: '#9ca3af' }}>Digite ao menos 2 caracteres</div>
            )}
            {resultados.map(c => (
              <button
                key={c.cid_cod}
                className={`nat-item${String(c.cid_cod) === codValue ? ' nat-item--ativo' : ''}`}
                onClick={() => selecionar(c)}
              >
                <div className="nat-item-linha1">
                  <span className="nat-item-cod">#{c.cid_cod}</span>
                  <span className="nat-item-cfop">{c.cid_estcod}</span>
                  {c.cid_codmun > 0 && (
                    <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>IBGE {c.cid_codmun}</span>
                  )}
                </div>
                <div className="nat-item-dsc">{c.cid_des}</div>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
